import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Nav } from "react-bootstrap";

const MegaMenuContainer = styled.div`
    position: relative;
`;

const MegaMenuTrigger = styled(Nav.Link)<{ active: boolean }>`
    cursor: pointer;
    font-size: 20px;
    color: ${props => props.active ? "rgba(255, 255, 255, 0.75)" : "rgba(255, 255, 255, 0.55)"} !important;
    
    &:hover {
        color: rgba(255, 255, 255, 0.75) !important;
    }
`;

const MegaMenuDropdown = styled.div<{ show: boolean }>`
    display: ${props => props.show ? "block" : "none"};
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    background-color: #343a40;
    border-top: 2px solid #007bff;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    padding: 30px 20px;
    min-width: 600px;
`;

const MegaMenuContent = styled.div`
    display: flex;
    gap: 60px;
    max-width: 1200px;
    margin: 0 auto;
`;

const MegaMenuSection = styled.div`
    flex: 1;
`;

const MegaMenuSectionTitle = styled.h6`
    color: #fff;
    font-weight: 600;
    margin-bottom: 15px;
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const MegaMenuList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const MegaMenuItem = styled.li`
    margin-bottom: 10px;
`;

const MegaMenuLink = styled(Link)`
    color: rgba(255, 255, 255, 0.75);
    text-decoration: none;
    font-size: 15px;
    display: block;
    padding: 5px 0;
    transition: color 0.2s;

    &:hover {
        color: #fff;
        text-decoration: none;
    }
`;

interface RouteConfig {
    path: string;
    displayName: string;
    groups: string[];
    hide?: boolean;
    children?: RouteConfig[];
}

interface AnalyticsMegaMenuProps {
    route: RouteConfig;
    activeKey: string;
}

const AnalyticsMegaMenu: React.FC<AnalyticsMegaMenuProps> = ({ route, activeKey }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 200);
    };

    const handleLinkClick = () => {
        setIsOpen(false);
    };

    const isActive = activeKey.startsWith(route.path);

    return (
        <MegaMenuContainer 
            ref={containerRef} 
            onMouseEnter={handleMouseEnter} 
            onMouseLeave={handleMouseLeave}
        >
            <MegaMenuTrigger active={isActive}>
                {route.displayName}
            </MegaMenuTrigger>
            <MegaMenuDropdown show={isOpen}>
                <MegaMenuContent>
                    {route.children?.filter((child: RouteConfig) => !child.hide).map((section: RouteConfig, index: number) => (
                        <MegaMenuSection key={index}>
                            <MegaMenuSectionTitle>{section.displayName}</MegaMenuSectionTitle>
                            <MegaMenuList>
                                {section.children?.map((childRoute: RouteConfig, childIndex: number) => (
                                    <MegaMenuItem key={childIndex}>
                                        <MegaMenuLink 
                                            to={childRoute.path}
                                            onClick={handleLinkClick}
                                        >
                                            {childRoute.displayName}
                                        </MegaMenuLink>
                                    </MegaMenuItem>
                                ))}
                            </MegaMenuList>
                        </MegaMenuSection>
                    ))}
                </MegaMenuContent>
            </MegaMenuDropdown>
        </MegaMenuContainer>
    );
};

export default AnalyticsMegaMenu;
